// Comprehensive student tracking dialog — shown from Admin → Users tab.
// Tabs: Profile · Enrollments · Attendance · Payments · Assignments
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  User, Mail, Phone, GraduationCap, IndianRupee, CalendarCheck, CheckCircle2,
  XCircle, Clock, BookOpen, Award, Plus, MapPin, Video, Loader2, AlertCircle,
} from "lucide-react";

const inp = "w-full bg-[#0a0a0a] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]";

export function StudentDetailDialog({ userId, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    if (!userId) return;
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get(`/admin/students/${userId}/overview`);
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load student");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [userId]); // eslint-disable-line

  const reload = async () => { await load(); onChange?.(); };

  if (!userId) return null;
  const u = data?.user;
  const sum = data?.summary;
  const totalPaidInr = sum ? Math.round((sum.total_paid_paise || 0) / 100) : 0;

  return (
    <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border border-white/10 max-w-5xl max-h-[92vh] overflow-hidden p-0 text-white" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
        {/* Header */}
        <DialogHeader className="px-6 py-5 border-b border-white/10 bg-gradient-to-r from-[#FF0066]/10 via-[#121212] to-[#0a0a0a]">
          <DialogTitle className="text-xl flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#FF0066] flex items-center justify-center font-bold text-base shrink-0">
              {(u?.name || "?")[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-white truncate" style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}>{u?.name || "Loading…"}</div>
              <div className="text-xs text-white/60 font-normal flex items-center gap-3 flex-wrap mt-0.5">
                {u?.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {u.email}</span>}
                {u?.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {u.phone}</span>}
                <span className="px-2 py-0.5 rounded bg-[#FF0066]/20 text-[#FF0066] text-[10px] uppercase tracking-wider font-bold">{u?.role || "student"}</span>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Summary strip */}
        {sum && (
          <div className="px-6 py-4 border-b border-white/10 grid grid-cols-2 md:grid-cols-4 gap-3 bg-[#050505]">
            <StatPill icon={GraduationCap} label="Enrollments" value={sum.total_enrollments} sub={`${sum.active_enrollments} active`} />
            <StatPill icon={IndianRupee} label="Total paid" value={`₹${totalPaidInr.toLocaleString("en-IN")}`} sub={`${sum.payments_count} transactions`} />
            <StatPill icon={CalendarCheck} label="Attendance" value={data?.enrollments?.length ? `${avgAttendance(data.enrollments)}%` : "—"} sub="across all batches" />
            <StatPill icon={Award} label="Assignments" value={countAssignments(data?.enrollments).submitted} sub={`of ${countAssignments(data?.enrollments).total}`} />
          </div>
        )}

        {loading && <div className="px-6 py-12 flex items-center justify-center gap-2 text-white/60"><Loader2 className="w-5 h-5 animate-spin" /> Loading student data…</div>}
        {err && <div className="px-6 py-8 text-center"><AlertCircle className="w-8 h-8 text-amber-400 mx-auto" /><div className="text-sm text-white/70 mt-2">{err}</div></div>}

        {data && (
          <div className="overflow-y-auto" style={{ maxHeight: "calc(92vh - 200px)" }}>
            <Tabs defaultValue="enrollments" className="px-6 py-4">
              <TabsList className="bg-[#121212] border border-white/10 rounded-lg">
                <TabsTrigger value="enrollments" className="data-[state=active]:bg-[#FF0066]/15 data-[state=active]:text-[#FF0066]" data-testid="tab-enrollments">Enrollments</TabsTrigger>
                <TabsTrigger value="attendance" className="data-[state=active]:bg-[#FF0066]/15 data-[state=active]:text-[#FF0066]" data-testid="tab-attendance">Attendance</TabsTrigger>
                <TabsTrigger value="assignments" className="data-[state=active]:bg-[#FF0066]/15 data-[state=active]:text-[#FF0066]" data-testid="tab-assignments">Assignments</TabsTrigger>
                <TabsTrigger value="payments" className="data-[state=active]:bg-[#FF0066]/15 data-[state=active]:text-[#FF0066]" data-testid="tab-payments">Payments</TabsTrigger>
              </TabsList>

              <TabsContent value="enrollments" className="mt-4 space-y-3">
                <EnrollSection user={u} enrollments={data.enrollments} onReload={reload} />
              </TabsContent>

              <TabsContent value="attendance" className="mt-4 space-y-4">
                {data.enrollments.length === 0 ? (
                  <Empty icon={CalendarCheck} text="No enrollments — attendance data will appear after first batch enrollment." />
                ) : (
                  data.enrollments.map((e) => <AttendanceCard key={e.enrollment_id} en={e} userId={u.id} onReload={reload} />)
                )}
              </TabsContent>

              <TabsContent value="assignments" className="mt-4 space-y-3">
                {data.enrollments.flatMap((e) => e.assignments.map((a) => ({ ...a, course_title: e.course_title }))).length === 0 ? (
                  <Empty icon={BookOpen} text="No assignments configured for this student's enrolled courses yet." />
                ) : (
                  data.enrollments.map((e) => e.assignments.length > 0 && (
                    <div key={e.enrollment_id} className="bg-[#121212] border border-white/10 rounded-lg p-4">
                      <div className="text-xs text-white/50 font-semibold uppercase tracking-wider mb-3">{e.course_title}</div>
                      <div className="space-y-2">
                        {e.assignments.map((a) => (
                          <div key={a.assignment_id} className="flex items-center justify-between p-2.5 bg-[#0a0a0a] rounded-md">
                            <div className="flex items-center gap-2.5 min-w-0">
                              {a.submitted ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" /> : <Clock className="w-4 h-4 text-amber-400 shrink-0" />}
                              <div className="min-w-0">
                                <div className="text-sm text-white truncate">{a.title}</div>
                                <div className="text-[11px] text-white/50">{a.submitted ? `Submitted ${a.submitted_at ? new Date(a.submitted_at).toLocaleDateString("en-IN") : ""}` : "Pending"}</div>
                              </div>
                            </div>
                            {a.marks != null && <span className="text-xs font-bold text-[#FF0066] shrink-0">{a.marks} marks</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </TabsContent>

              <TabsContent value="payments" className="mt-4">
                {data.payments.length === 0 ? <Empty icon={IndianRupee} text="No payments recorded yet." /> : (
                  <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-[#0a0a0a] text-[11px] uppercase text-white/50">
                        <tr><th className="text-left px-4 py-2.5">Date</th><th className="text-left px-4 py-2.5">Course</th><th className="text-left px-4 py-2.5">Amount</th><th className="text-left px-4 py-2.5">Mode</th><th className="text-left px-4 py-2.5">Status</th></tr>
                      </thead>
                      <tbody>
                        {data.payments.map((p) => (
                          <tr key={p.id} className="border-t border-white/5">
                            <td className="px-4 py-2.5 text-white/80 text-xs">{p.paid_at ? new Date(p.paid_at).toLocaleDateString("en-IN") : "—"}</td>
                            <td className="px-4 py-2.5 text-white/80 truncate max-w-xs">{p.course_id?.slice(0, 8) || "—"}</td>
                            <td className="px-4 py-2.5 font-semibold text-white">₹{Math.round((p.amount || 0) / 100).toLocaleString("en-IN")}</td>
                            <td className="px-4 py-2.5 text-white/70 text-xs uppercase">{p.payment_mode || p.via || "online"}</td>
                            <td className="px-4 py-2.5"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${p.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{p.status || "—"}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatPill({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-[#121212] border border-white/10 rounded-lg px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/50 font-semibold">
        <Icon className="w-3 h-3 text-[#FF0066]" /> {label}
      </div>
      <div className="text-lg font-bold text-white mt-0.5" style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}>{value}</div>
      {sub && <div className="text-[10px] text-white/40">{sub}</div>}
    </div>
  );
}

function Empty({ icon: Icon, text }) {
  return (
    <div className="bg-[#121212] border border-white/10 rounded-lg p-10 text-center">
      <Icon className="w-10 h-10 text-white/20 mx-auto mb-3" />
      <div className="text-sm text-white/60">{text}</div>
    </div>
  );
}

function avgAttendance(enrolls) {
  const a = enrolls.filter((e) => e.attendance?.total_held > 0).map((e) => e.attendance.percent);
  if (a.length === 0) return "0";
  return Math.round(a.reduce((x, y) => x + y, 0) / a.length);
}
function countAssignments(enrolls) {
  if (!enrolls) return { submitted: 0, total: 0 };
  let s = 0, t = 0;
  enrolls.forEach((e) => e.assignments.forEach((a) => { t++; if (a.submitted) s++; }));
  return { submitted: s, total: t };
}

// ===== Enrollments tab =====
function EnrollSection({ user, enrollments, onReload }) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="space-y-3">
      {enrollments.length === 0 ? (
        <Empty icon={GraduationCap} text="Yeh student kisi course me enrolled nahi hai. Niche se enroll karein." />
      ) : (
        enrollments.map((e) => <EnrollmentCard key={e.enrollment_id} en={e} onReload={onReload} />)
      )}

      {adding ? (
        <AddEnrollmentForm user={user} onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); onReload(); }} />
      ) : (
        <button onClick={() => setAdding(true)} className="w-full inline-flex items-center justify-center gap-2 bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-lg px-4 py-3 transition shadow-[0_8px_25px_-8px_rgba(255,0,102,0.5)]" data-testid="add-enrollment-btn">
          <Plus className="w-4 h-4" /> Enroll this student in a batch
        </button>
      )}
    </div>
  );
}

function EnrollmentCard({ en, onReload }) {
  const [batches, setBatches] = useState([]);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (editing) api.get(`/batches?course_id=${en.course_id}`).then(({ data }) => setBatches(data));
  }, [editing, en.course_id]);
  const moveBatch = async (batch_id) => {
    try {
      await api.patch(`/admin/enrollments/${en.enrollment_id}`, { batch_id: batch_id || null });
      toast.success("Batch updated");
      setEditing(false); onReload();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-[#121212] border border-white/10 rounded-lg p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-semibold text-white">{en.course_title}</div>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${en.status === "active" ? "bg-emerald-500/20 text-emerald-300" : "bg-white/5 text-white/50"}`}>{en.status}</span>
          </div>
          {en.batch ? (
            <div className="mt-2 flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-white/60">
              <span className="font-semibold text-white/80">{en.batch.name}</span>
              <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${en.batch.mode === "offline" ? "bg-emerald-500/20 text-emerald-300" : en.batch.mode === "hybrid" ? "bg-blue-500/20 text-blue-300" : "bg-[#FF0066]/20 text-[#ff6699]"}`}>{en.batch.mode || "online"}</span>
              {en.batch.timing && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {en.batch.timing}</span>}
              {en.batch.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {en.batch.location.split(",")[0]}</span>}
              {!en.batch.location && en.batch.meeting_provider && <span className="inline-flex items-center gap-1"><Video className="w-3 h-3" /> {en.batch.meeting_provider}</span>}
            </div>
          ) : <div className="mt-1 text-xs text-amber-300">⚠️ Not assigned to any batch</div>}
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => setEditing(!editing)} className="text-xs bg-white/5 hover:bg-white/10 text-white border border-white/15 rounded-md px-3 py-1.5 transition" data-testid={`move-batch-${en.enrollment_id}`}>{editing ? "Cancel" : "Move batch"}</button>
        </div>
      </div>
      {editing && (
        <div className="mt-3 pt-3 border-t border-white/5">
          <div className="text-[11px] text-white/50 mb-1.5 uppercase font-semibold">Choose target batch</div>
          <div className="grid grid-cols-1 gap-1.5 max-h-48 overflow-y-auto">
            <button onClick={() => moveBatch("")} className="text-left text-xs px-3 py-2 rounded bg-white/5 hover:bg-white/10 text-white/70">— No batch —</button>
            {batches.length === 0 && <div className="text-xs text-white/40 px-3 py-2">No batches for this course. Create one in Batches tab first.</div>}
            {batches.map((b) => (
              <button key={b.id} onClick={() => moveBatch(b.id)} className="text-left text-xs px-3 py-2 rounded bg-white/5 hover:bg-[#FF0066]/15 hover:border-[#FF0066]/30 border border-transparent text-white flex items-center justify-between gap-2 transition">
                <span className="truncate">{b.name} <span className="text-white/40 ml-1">({b.enrolled_count || 0}/{b.capacity})</span></span>
                <span className={`shrink-0 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${b.mode === "offline" ? "bg-emerald-500/20 text-emerald-300" : b.mode === "hybrid" ? "bg-blue-500/20 text-blue-300" : "bg-[#FF0066]/20 text-[#ff6699]"}`}>{b.mode || "online"}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Mini stats */}
      <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-3 gap-2 text-center">
        <Mini label="Progress" value={`${en.progress || 0}%`} />
        <Mini label="Attended" value={`${en.attendance?.present || 0}/${en.attendance?.total_held || 0}`} />
        <Mini label="Paid" value={`₹${Math.round((en.paid_amount_paise || 0) / 100).toLocaleString("en-IN")}`} />
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-[#0a0a0a] rounded-md py-1.5">
      <div className="text-[9px] uppercase text-white/40 tracking-wider">{label}</div>
      <div className="text-sm font-bold text-white">{value}</div>
    </div>
  );
}

function AddEnrollmentForm({ user, onCancel, onSaved }) {
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [f, setF] = useState({ course_id: "", batch_id: "", amount: 0, payment_mode: "cash", send_credentials_email: false });
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { api.get("/courses").then(({ data }) => setCourses(data)); }, []);
  useEffect(() => {
    if (f.course_id) api.get(`/batches?course_id=${f.course_id}`).then(({ data }) => setBatches(data));
    else setBatches([]);
  }, [f.course_id]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/admin/manual-enroll", {
        name: user.name,
        email: user.email,
        phone: user.phone || "",
        course_id: f.course_id,
        batch_id: f.batch_id || null,
        amount: Number(f.amount) || 0,
        payment_mode: f.payment_mode,
        send_credentials_email: false,
      });
      toast.success("Student enrolled!");
      onSaved();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Failed to enroll");
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="bg-[#121212] border border-[#FF0066]/30 rounded-lg p-4 space-y-3" data-testid="add-enrollment-form">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold text-white">Add new enrollment</div>
        <button type="button" onClick={onCancel} className="text-xs text-white/50 hover:text-white">Cancel</button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <div className="text-xs text-white/60 mb-1">Course *</div>
          <select required value={f.course_id} onChange={(e) => setF({ ...f, course_id: e.target.value, batch_id: "" })} className={inp} data-testid="enroll-course">
            <option value="">Select course…</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-xs text-white/60 mb-1">Batch (optional)</div>
          <select value={f.batch_id} onChange={(e) => setF({ ...f, batch_id: e.target.value })} className={inp} data-testid="enroll-batch" disabled={!f.course_id}>
            <option value="">— No batch —</option>
            {batches.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.mode || "online"}) — {b.enrolled_count || 0}/{b.capacity}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-xs text-white/60 mb-1">Amount paid (₹)</div>
          <input type="number" min="0" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={inp} />
        </label>
        <label className="block">
          <div className="text-xs text-white/60 mb-1">Payment mode</div>
          <select value={f.payment_mode} onChange={(e) => setF({ ...f, payment_mode: e.target.value })} className={inp}>
            {["cash", "upi", "bank_transfer", "card", "scholarship"].map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
          </select>
        </label>
      </div>
      <button disabled={submitting || !f.course_id} className="w-full bg-[#FF0066] hover:bg-[#e6005c] disabled:opacity-50 text-white font-semibold rounded-lg px-4 py-2.5 text-sm transition" data-testid="submit-enrollment">
        {submitting ? "Enrolling…" : "Enroll Student"}
      </button>
    </form>
  );
}

// ===== Attendance tab =====
function AttendanceCard({ en, userId, onReload }) {
  const [marking, setMarking] = useState(null); // class_id being marked
  const mark = async (class_id, status) => {
    setMarking(class_id);
    try {
      await api.post("/admin/attendance/manual", { user_id: userId, class_id, status });
      toast.success(`Marked ${status}`);
      onReload();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Failed");
    } finally { setMarking(null); }
  };

  const a = en.attendance;
  return (
    <div className="bg-[#121212] border border-white/10 rounded-lg p-4">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="text-sm font-semibold text-white">{en.course_title}</div>
          {en.batch && (
            <div className="text-xs text-white/50 mt-0.5 flex items-center gap-2 flex-wrap">
              <span>{en.batch.name}</span>
              <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${en.batch.mode === "offline" ? "bg-emerald-500/20 text-emerald-300" : "bg-[#FF0066]/20 text-[#ff6699]"}`}>{en.batch.mode || "online"}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-2xl font-bold text-white" style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}>{a.percent}%</div>
            <div className="text-[10px] text-white/50 uppercase tracking-wider">{a.present}/{a.total_held} attended</div>
          </div>
          <div className="w-16 h-16 rounded-full border-4 border-[#FF0066]/20 relative flex items-center justify-center" style={{ background: `conic-gradient(#FF0066 ${a.percent}%, transparent ${a.percent}%)` }}>
            <div className="absolute inset-1 rounded-full bg-[#121212]" />
            <span className="relative text-xs font-bold text-white">{a.percent}%</span>
          </div>
        </div>
      </div>
      {en.classes.length === 0 ? (
        <div className="text-xs text-white/40 text-center py-3">No classes scheduled for this batch yet.</div>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {en.classes.map((c) => (
            <div key={c.class_id} className="flex items-center justify-between p-2.5 bg-[#0a0a0a] rounded-md text-xs">
              <div className="flex items-center gap-2.5 min-w-0">
                {c.status === "present" && <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />}
                {c.status === "absent" && <XCircle className="w-4 h-4 text-red-400 shrink-0" />}
                {c.status === "late" && <Clock className="w-4 h-4 text-amber-400 shrink-0" />}
                {c.status === "upcoming" && <Clock className="w-4 h-4 text-white/40 shrink-0" />}
                <div className="min-w-0">
                  <div className="text-white truncate">{c.title}</div>
                  <div className="text-[10px] text-white/40">{new Date(c.scheduled_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
              {c.status !== "upcoming" && (
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => mark(c.class_id, "present")} disabled={marking === c.class_id} className={`text-[10px] px-2 py-1 rounded font-bold uppercase tracking-wider transition ${c.status === "present" ? "bg-emerald-500 text-white" : "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/30"}`}>P</button>
                  <button onClick={() => mark(c.class_id, "absent")} disabled={marking === c.class_id} className={`text-[10px] px-2 py-1 rounded font-bold uppercase tracking-wider transition ${c.status === "absent" ? "bg-red-500 text-white" : "bg-red-500/15 text-red-300 hover:bg-red-500/30"}`}>A</button>
                  <button onClick={() => mark(c.class_id, "late")} disabled={marking === c.class_id} className={`text-[10px] px-2 py-1 rounded font-bold uppercase tracking-wider transition ${c.status === "late" ? "bg-amber-500 text-white" : "bg-amber-500/15 text-amber-300 hover:bg-amber-500/30"}`}>L</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
